/**
 * Memória de quais fotos de produto existem no blob.
 *
 * O tooltip do ECharts é HTML cru remontado pelo `formatter` a CADA mousemove — não a cada hover.
 * Sem esta memória, todo frame cria um `<img>` novo, que nasce oculto e só aparece no `load`:
 * mexendo o mouse dentro do mesmo ponto, a foto pisca dezenas de vezes por segundo. E como a URL
 * é gravada por convenção (o arquivo pode não existir), o mesmo 404 era re-solicitado a cada
 * frame.
 *
 * Com o desfecho em memória, o formatter decide antes de montar o HTML: foto conhecida sai já
 * visível, ausente não sai. Uma requisição por URL, na primeira vez, e nunca mais.
 */
export type ImageState = 'unknown' | 'loaded' | 'failed';

const states = new Map<string, ImageState>();

/** `new Image()` real; o teste injeta um dublê para não depender de rede. */
type ProbeFactory = () => HTMLImageElement;
const defaultProbeFactory: ProbeFactory = () => new Image();

export function imageState(url: string): ImageState {
  return states.get(url) ?? 'unknown';
}

/**
 * Descobre se a URL existe, uma vez só. Chamadas repetidas para a mesma URL — o caso normal, dado
 * o mousemove — não fazem nada: o estado `unknown` só é gravado quando a sondagem começa, e é ele
 * que impede a segunda.
 */
export function trackImage(
  url: string,
  probeFactory: ProbeFactory = defaultProbeFactory,
): void {
  if (states.has(url)) return;
  states.set(url, 'unknown');

  const probe = probeFactory();
  probe.onload = () => states.set(url, 'loaded');
  probe.onerror = () => states.set(url, 'failed');
  probe.src = url;
}

export function resetImageCacheForTest(): void {
  states.clear();
}
